import { useRouteContext, useRouter, useSearch } from "@tanstack/react-router";
import { convertDateTimeIgnoringTimezone } from "@repo/lib";
import type { PatientsPaymentsQueryQuery } from "@repo/graphql-types/graphql";
import { useMemo } from "react";
import { X } from "@repo/icons";
import { CpsBadge } from "corpus";
import { PaymentFilterMenuDrawer } from "@/components/payment-filter-menu-drawer";
import { AmountTitle } from "@/components/amount-title";
import { PatientPaymentsList } from "@/components/patient-payments-list";
import { PaymentDateFilterForm } from "@/components/payment-date-filter-form";
import {
  PaymentsActionButtons,
  PaymentsContent,
  PaymentsHeaderContainer,
  PaymentsRoot,
} from "@/components/payments-root";
import { paymentStatusLabelMap } from "@/enums/payment-status";
import { paymentOriginLabelMap } from "@/enums/payment-origin";
import { paymentMethodLabelMap } from "@/enums/payment-method";

interface PaymentsFilterData {
  filter: string;
  value: string;
  label: string;
}

interface PaymentsListContainerProps {
  data: PatientsPaymentsQueryQuery | undefined;
  intervalStart: string;
  intervalEnd: string;
}

export const PaymentsListContainer = ({
  data,
  intervalStart,
  intervalEnd,
}: PaymentsListContainerProps): JSX.Element => {
  const router = useRouter();
  const searchParams = useSearch({
    from: "/settings/payments/patient-payments/",
  });

  const getAllFilters = (): PaymentsFilterData[] => {
    const statusFilters = (searchParams.status ?? []).map((value: string) => ({
      filter: "status",
      value,
      label: paymentStatusLabelMap[value],
    }));

    const originFilters = (searchParams.origins ?? []).map((value: string) => ({
      filter: "origins",
      value,
      label: paymentOriginLabelMap[value],
    }));

    const paymentMethodFilters = (searchParams.paymentMethods ?? []).map(
      (value: string) => ({
        filter: "paymentMethods",
        value,
        label: paymentMethodLabelMap[value],
      }),
    );

    return [...statusFilters, ...originFilters, ...paymentMethodFilters];
  };

  const paymentFilterValue = {
    from: convertDateTimeIgnoringTimezone(intervalStart),
    to: convertDateTimeIgnoringTimezone(intervalEnd),
  };

  const setOpenDrawer = (open: boolean, paramName: "filter"): void => {
    if (open) {
      searchParams[paramName] = open;

      void router.navigate({ search: searchParams });
    } else if (searchParams[paramName]) {
      router.history.back();
    }
  };

  const setOpenFilter = (open: boolean): void => {
    setOpenDrawer(open, "filter");
  };

  const setDrawerDismiss = (value: boolean): void => {
    if (!value && searchParams.action === "show-filter") {
      void router.navigate({
        search: { ...searchParams, action: undefined },
        replace: true,
      });
    }
  };

  const { flags } = useRouteContext({ strict: false });
  const isNewFilterMenuAvailable = useMemo(
    () => flags["exibe-nova-experiencia-de-filtros"],
    [flags],
  );

  let allFilters: PaymentsFilterData[] = [];

  if (isNewFilterMenuAvailable) {
    allFilters = getAllFilters();
  }

  const removeFilterValue = (filterName: string, valueToRemove: string): void => {
    const filterArray = Array.isArray(searchParams[filterName])
      ? (searchParams[filterName] as string[])
      : [];
    const updatedFilter = filterArray.filter((value: string) => value !== valueToRemove);

    searchParams[filterName] = updatedFilter.length > 0 ? updatedFilter : undefined;

    void router.navigate({ search: searchParams });
  };

  return (
    <PaymentsRoot>
      {isNewFilterMenuAvailable ? (
        <PaymentsHeaderContainer>
          <AmountTitle
            title="Valor total"
            amount={data?.patientPayments?.totalAmount ?? 0}
          />
          <PaymentsActionButtons>
            <PaymentDateFilterForm
              open={Boolean(searchParams.filter)}
              setOpenFilter={setOpenFilter}
              value={paymentFilterValue}
            />
            <PaymentFilterMenuDrawer setOpenFilter={setDrawerDismiss} />
          </PaymentsActionButtons>
          {allFilters.length > 0 && (
            <div className="flex flex-row flex-wrap gap-2">
              {allFilters.map((item) => (
                <CpsBadge
                  key={`${item.filter}-${item.value}`}
                  onClick={() => removeFilterValue(item.filter, item.value)}
                  className="bg-gray-300"
                  size="large"
                >
                  {item.label.toUpperCase()}
                  <X size={12} className="text-gray-600 ml-1" />
                </CpsBadge>
              ))}
            </div>
          )}
        </PaymentsHeaderContainer>
      ) : (
        <>
          <AmountTitle
            title="Valor total"
            amount={data?.patientPayments?.totalAmount ?? 0}
          />
          <PaymentsActionButtons>
            <PaymentDateFilterForm
              open={Boolean(searchParams.filter)}
              setOpenFilter={setOpenFilter}
              value={paymentFilterValue}
            />
          </PaymentsActionButtons>
        </>
      )}
      <PaymentsContent>
        {data?.patientPayments ? (
          <PatientPaymentsList data={data.patientPayments} />
        ) : null}
      </PaymentsContent>
    </PaymentsRoot>
  );
};
